import keyBy from 'lodash/keyBy';

import { TAvailableActions } from '../../../../../types/globalTypes';
import { ESubscriberIdActions } from '../../../types';
import { IPersonalDataDocType, IPersonalDataMeta, PersonalDataType } from '../types/meta';


export interface IPersonalDataMetaResponse {
    actions: TAvailableActions<ESubscriberIdActions>;
    areAllAttributesOptional: boolean;
    docTypes: IPersonalDataDocTypeResponse[];
    maxFileSize: number;
    passportId: number;
    personalDataHidden: boolean;
    privileged: boolean;
    subscriber: boolean;
    uploadFilesAmount: number;
}

export interface IPersonalDataDocTypeResponse {
    identificationTypeId: number;                       // Идентификатор типа документа
    name: string;                                       // Наименование типа документа
    mandatoryAttributes: PersonalDataType[];            // Список обязательных атрибутов для документа
    documentAttributes: PersonalDataType[];             // Список доступных атрибутов для документа
    seriesRegExp?: string;                              // Регулярное выражение для проверки серии документа
    numRegExp?: string;                                 // Регулярное выражение для проверки номера документа
    isResident: boolean;                                // Признак документа, определяющего резиденство
    nationalityInfo?: {                                 // Страна резиденства для документа
        addressId: string;
        addressString: string;
    };
}

const mapSubscriberIdPersonalDataDocTypeMapping =
    ({ identificationTypeId, nationalityInfo, ...response }: IPersonalDataDocTypeResponse): IPersonalDataDocType => ({
        id: identificationTypeId,
        nationalityInfo: nationalityInfo
            ? { id: nationalityInfo.addressId, def: nationalityInfo.addressString || '' }
            : undefined,
        ...response,
    });


const mapRequiredPersonalDataFields = {
    areAllAttributesOptional: {
        documentAttributes: [
            PersonalDataType.address,
            PersonalDataType.birthDate,
            PersonalDataType.birthPlace,
            PersonalDataType.email,
            PersonalDataType.files,
            PersonalDataType.name,
            PersonalDataType.patronymic,
            PersonalDataType.surname,
        ],
        mandatoryAttributes: [],
    },
    privileged: {
        documentAttributes: [
            PersonalDataType.address,
            PersonalDataType.birthDate,
            PersonalDataType.files,
            PersonalDataType.name,
            PersonalDataType.patronymic,
        ],
        mandatoryAttributes: [
            PersonalDataType.address,
            PersonalDataType.birthDate,
            PersonalDataType.files,
            PersonalDataType.name,
            PersonalDataType.patronymic,
        ],
    },
    common: {
        documentAttributes: [
            PersonalDataType.address,
            PersonalDataType.birthDate,
            PersonalDataType.birthPlace,
            PersonalDataType.email,
            PersonalDataType.files,
            PersonalDataType.name,
            PersonalDataType.patronymic,
            PersonalDataType.surname,
        ],
        mandatoryAttributes: [
            PersonalDataType.address,
            PersonalDataType.birthDate,
            PersonalDataType.files,
            PersonalDataType.name,
            PersonalDataType.patronymic,
            PersonalDataType.surname,
        ],
    },
};

export const personalDataMetaMapping =
    (response: IPersonalDataMetaResponse): IPersonalDataMeta => {
        // doc types
        const docTypes: IPersonalDataDocType[] =
            response.docTypes?.map(mapSubscriberIdPersonalDataDocTypeMapping) ?? [];

        // personal data types
        let personalDataTypes = mapRequiredPersonalDataFields.common;
        if (response.areAllAttributesOptional) {
            personalDataTypes = mapRequiredPersonalDataFields.areAllAttributesOptional;
        } else if (response.privileged) {
            personalDataTypes = mapRequiredPersonalDataFields.privileged;
        }
        // actions
        const actions: TAvailableActions<ESubscriberIdActions> = {
            [ESubscriberIdActions.personalDataConfirm]: {
                isVisible: response.actions[ESubscriberIdActions.personalDataConfirm]?.isVisible ?? false,
                isDisabled: response.actions[ESubscriberIdActions.personalDataConfirm]?.isDisabled ?? false,
            },
            [ESubscriberIdActions.personalDataEdit]: {
                isVisible: true,
                isDisabled: response.actions[ESubscriberIdActions.personalDataEdit]?.isDisabled ?? false,
                reason: response.actions[ESubscriberIdActions.personalDataEdit]?.reason,
            },
            [ESubscriberIdActions.personalDataDelete]: {
                isVisible: response.actions[ESubscriberIdActions.personalDataDelete]?.isVisible ?? false,
                isDisabled: response.actions[ESubscriberIdActions.personalDataDelete]?.isDisabled ?? false,
                conflicts: response.actions[ESubscriberIdActions.personalDataDelete]?.conflicts,
            },
        };

        return {
            actions,
            docTypes: keyBy(docTypes, 'id'),
            maxFileAmount: response.uploadFilesAmount || 1,
            maxFileSize: response.maxFileSize || 0,
            options: docTypes.map(doc => ({ value: doc.id, label: doc.name })),
            passportId: response.passportId || 0,
            personalDataTypes,
        };
    };
